// Map "Colour by Scope" (issue #24). Pure helpers: which colour a repeater marker gets, its tooltip, and the
// legend counts. The scopes themselves come from GET /api/audit/map-scopes (the stored Audit table), never
// computed here.

export type ColourBy = "role" | "scope";
export const COLOUR_BY_OPTIONS: { id: ColourBy; label: string }[] = [
  { id: "role", label: "Role" },
  { id: "scope", label: "Scope" },
];
export const COLOUR_BY_STORAGE_KEY = "amalgamesh:mapColourBy";
export const MULTIPLE_LABEL = "Multiple";
export const UNKNOWN_LABEL = "Unknown";
export const OTHER_LABEL = "Other";
/** Shown under the Scope legend: only MeshCore repeaters are coloured by scope. */
export const MESHTASTIC_NOT_COLOURED_NOTE = "Meshtastic nodes keep their role shade";
export const NON_REPEATER_NOTE = "Other MeshCore roles keep their role shade, dimmed";
export const SCOPES_MESHCORE_ONLY_NOTE = "Scopes are MeshCore only";
/** Scope colouring and Region reach only mean something while MeshCore nodes are shown: the protocol filter is All or MeshCore. */
export function scopesAvailable(protocolFilter: string): boolean {
  return protocolFilter !== "meshtastic";
}
/** The FreqMapper coverage layer only has Meshtastic evidence, so it is drawn while Meshtastic nodes are shown: the protocol filter is All or Meshtastic. */
export function meshtasticShown(protocolFilter: string): boolean {
  return protocolFilter !== "meshcore";
}
/** The colour-by mode actually in force: Scope falls back to Role while the filter hides MeshCore. */
export function effectiveColourBy(chosen: ColourBy, protocolFilter: string): ColourBy {
  return scopesAvailable(protocolFilter) ? chosen : "role";
}
/** Meshtastic routers (the whole Router / Repeater / Router + client group) are drawn this many times the normal dot radius. */
export const ROUTER_DOT_SCALE = 1.5;
/** In Scope mode a MeshCore repeater with no known scope is drawn this much smaller than a normal dot, so it recedes. */
export const UNKNOWN_SCOPE_DOT_SCALE = 0.75;

/** Radius factor of one marker: 1.5 for a Meshtastic router, 0.75 for an Unknown-scope repeater, else 1. MeshCore repeaters stay normal. */
export function dotScale(protocol: string, roleCategory: string | null, scopeKind: string | null): number {
  if (protocol === "meshtastic" && roleCategory === "router_repeater") return ROUTER_DOT_SCALE;
  if (scopeKind === "unknown") return UNKNOWN_SCOPE_DOT_SCALE;
  return 1;
}

/** Drawing order (higher is drawn on top): big routers at the bottom, then Unknown-scope repeaters, then ordinary dots, then repeaters with a real scope. */
export function drawOrder(protocol: string, roleCategory: string | null, scopeKind: string | null): number {
  if (protocol === "meshtastic" && roleCategory === "router_repeater") return 0;
  if (scopeKind === "unknown") return 1;
  if (scopeKind === "single" || scopeKind === "multiple" || scopeKind === "other") return 3;
  return 2;
}

/** Markers that are not repeaters keep their normal colour but are dimmed in Scope mode. */
export const NON_REPEATER_DIM = 0.35;

export interface MapScopesPayload {
  pending?: boolean;
  nodes: Record<string, string[]>;
  scopes: { name: string; colour: string; custom: boolean; other?: boolean; repeaters?: number }[];
  multiple_colour: string;
  unknown_colour: string;
  other_colour?: string;
}

export interface ScopeMarker {
  colour: string;
  /** "single" with the scope name, "multiple", or "unknown". */
  kind: "single" | "multiple" | "unknown" | "other";
  scope: string | null;
  tip: string;
}

export function parseColourBy(value: string | null | undefined): ColourBy {
  return value === "scope" ? "scope" : "role";
}

export function isMapScopes(data: unknown): data is MapScopesPayload {
  const d = data as MapScopesPayload | null;
  return !!d && !d.pending && typeof d.nodes === "object" && d.nodes !== null && Array.isArray(d.scopes);
}

/** The colour, kind and tooltip text of one repeater. A repeater the Audit table has no scope for is Unknown. */
export function scopeMarker(nodeId: string, name: string, view: MapScopesPayload): ScopeMarker {
  const scopes = view.nodes[nodeId] ?? [];
  if (scopes.length === 0) {
    return { colour: view.unknown_colour, kind: "unknown", scope: null, tip: `${name} (Scope: ${UNKNOWN_LABEL})` };
  }
  if (scopes.length > 1) {
    return { colour: view.multiple_colour, kind: "multiple", scope: null, tip: `${name} (Scopes: ${scopes.join(", ")})` };
  }
  const found = view.scopes.find((s) => s.name === scopes[0]);
  if (found?.other) {
    return { colour: found.colour, kind: "other", scope: scopes[0], tip: `${name} (Scope: ${scopes[0]}, shown as ${OTHER_LABEL})` };
  }
  return { colour: found?.colour ?? view.unknown_colour, kind: "single", scope: scopes[0], tip: `${name} (Scope: ${scopes[0]})` };
}

export interface LegendEntry {
  key: string;
  label: string;
  colour: string;
  count: number;
}

/** Legend rows from the repeater markers actually on the map: one per coloured scope that has a marker, then Other
 * (every scope without a colour), Multiple, Unknown. */
export function legendEntries(markers: ScopeMarker[], view: MapScopesPayload): LegendEntry[] {
  const per = new Map<string, number>();
  let multiple = 0;
  let unknown = 0;
  let other = 0;
  for (const m of markers) {
    if (m.kind === "single" && m.scope) per.set(m.scope, (per.get(m.scope) ?? 0) + 1);
    else if (m.kind === "other") other += 1;
    else if (m.kind === "multiple") multiple += 1;
    else unknown += 1;
  }
  const out: LegendEntry[] = view.scopes
    .filter((s) => per.has(s.name) && !s.other)
    .map((s) => ({ key: s.name, label: s.name, colour: s.colour, count: per.get(s.name) ?? 0 }));
  if (other > 0) out.push({ key: "__other", label: OTHER_LABEL, colour: view.other_colour ?? view.unknown_colour, count: other });
  if (multiple > 0) out.push({ key: "__multiple", label: MULTIPLE_LABEL, colour: view.multiple_colour, count: multiple });
  if (unknown > 0) out.push({ key: "__unknown", label: UNKNOWN_LABEL, colour: view.unknown_colour, count: unknown });
  return out;
}


// --- Colour maths ---------------------------------------------------------------------------------------------
function channel(v: number): number {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of "#rrggbb"; null for anything else. */
export function luminance(hex: string): number | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

/** WCAG contrast ratio of two "#rrggbb" colours (1 to 21); 1 when either is not a hex colour. */
export function contrastRatio(a: string, b: string): number {
  const x = luminance(a);
  const y = luminance(b);
  if (x === null || y === null) return 1;
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
